import api from './api';
import { ApiResponse, unwrapApiResponse } from '@/utils/api-response.util';
import { CalculatedMetricWithExplanation } from '../types/metrics';

export interface CalculationParams {
  height: number | null;
  weight: number | null;
  gender: string | null;
  age: number | null;
  activityLevel: string | null;
  waist: number | null;
  hips: number | null;
}

export interface CalculationOptions {
  gender: string[];
  activityLevel: string[];
}

export interface CalculationParamsResponse {
  params: CalculationParams;
  options: CalculationOptions;
}

export const getCalculatedMetrics = async (): Promise<CalculatedMetricWithExplanation[]> => {
  try {
    const response = await api.get<ApiResponse<CalculatedMetricWithExplanation[]>>(`/user-metrics/all-calculated`);
    const data = unwrapApiResponse<CalculatedMetricWithExplanation[]>(response);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error fetching calculated metrics:', error);
    throw error;
  }
};

export const getClientCalculatedMetrics = async (clientId: string): Promise<CalculatedMetricWithExplanation[]> => {
    try {
      const response = await api.get<ApiResponse<CalculatedMetricWithExplanation[]>>(`/client/${clientId}/user-metrics/all-calculated`);
      const data = unwrapApiResponse<CalculatedMetricWithExplanation[]>(response);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Error fetching client calculated metrics:', error);
      throw error;
    }
};

export const getCalculatedMetricValues = async (userId?: number): Promise<Record<string, number | null>> => {
  try {
    // If userId is provided, we might want to use a different endpoint or logic,
    // but based on the error, the backend expects /user-metrics/values without ID for the current user.
    // If we need to fetch for another user (e.g. expert view), we should probably use a different endpoint like /client/:id/user-metrics/values
    // For now, let's fix the immediate error for the current user.
    
    const url = userId ? `/user-metrics/values` : `/user-metrics/values`; 
    // Note: If userId is meant for a specific client view, the backend endpoint might need to be different.
    // However, the error "Cannot GET /api/v1/user-metrics/21/values" clearly shows the issue is the ID in the path.
    // Assuming this function is primarily used for the current user context based on GoalsSettings.tsx usage.

    const response = await api.get<ApiResponse<Record<string, number | null>>>(url);
    return unwrapApiResponse<Record<string, number | null>>(response);
  } catch (error) {
    console.error('Error fetching calculated metric values:', error);
    throw error;
  }
};

export const getCalculationParams = async (): Promise<CalculationParamsResponse> => {
  try {
    const response = await api.get<ApiResponse<CalculationParamsResponse>>('/user-metrics/calculation-params');
    return unwrapApiResponse<CalculationParamsResponse>(response);
  } catch (error) {
    console.error('Error fetching calculation params:', error);
    throw error;
  }
};
